import { useMemo } from 'react';
import { Button, useGlobalToast } from '@repo/atomic-components';
import {
  api,
  BUNDLE_DUPLICATES_TAG,
  BUNDLE_SIMILARS_TAG,
  BUNDLE_UNDO_REDO_HISTORY_TAG,
  isEndpointLockError,
  refreshBundleSectionsWithCurrentFilters,
  useAddToLightBundleMutation,
} from '@/api';
import {
  BROADCAST_FORCE_CASE_OVERVIEW_CARDS_REFRESH,
  BROADCAST_FORCE_TIMELINE_REFRESH,
  BROADCAST_SCROLL_TO_PAGE_ID_IN_LIGHT_BUNDLE_WHEN_EXTRACTED_FROM_FULL_BUNDLE,
  useFletchersBroadcast,
} from '@/broadcast';
import { AccessControlAction } from '@/constants';
import { FILTER_EXTRACTED_KEY } from '@/constants/filters';
import { useAccessControl } from '@/hooks';
import {
  BundleType,
  makeSelectBundlePageDataByPageId,
  mergePageInfo,
  PageInfoData,
  selectBundleMetadataByStoreKey,
  selectBundlePageIds,
  selectEnabledStates,
  selectPageInfoCache,
  selectSharedDataActiveBundleType,
  selectSharedDataBundleId,
  store,
  useAppDispatch,
  useAppSelector,
} from '@/store';

interface ExtractMatchingPagesButtonProps {
  /** Page IDs of the current search matches, in bundle order. */
  pageIds: string[];
}

export const ExtractMatchingPagesButton = ({ pageIds }: ExtractMatchingPagesButtonProps) => {
  const dispatch = useAppDispatch();
  const globalToastRef = useGlobalToast();
  const { sendMessageWithoutResponse, sendMessageWithResponse } = useFletchersBroadcast();
  const isEditBundles = useAccessControl(AccessControlAction.EDIT_BUNDLES);

  const bundleId = useAppSelector(selectSharedDataBundleId);
  const activeBundleType = useAppSelector(selectSharedDataActiveBundleType);
  const currentBundleMetadata = useAppSelector(selectBundleMetadataByStoreKey());
  const otherBundleId = currentBundleMetadata?.otherBundleId;
  const pageInfoCache = useAppSelector(selectPageInfoCache);
  const bundlePageIds = useAppSelector(selectBundlePageIds);
  const enabledStates = useAppSelector(selectEnabledStates);
  const getBundlePageDataByPageId = useAppSelector(makeSelectBundlePageDataByPageId);

  const [triggerAddToLightBundle, { isLoading }] = useAddToLightBundleMutation();

  const pageIdsNotInLightBundle = useMemo(
    () =>
      pageIds.filter((id) => {
        const cached = pageInfoCache[id]?.inLightBundle;
        // Use cached value if available, or fallback to item data
        const isInLightBundle = cached ?? getBundlePageDataByPageId(id)?.inLightBundle;
        return !isInLightBundle;
      }),
    [pageIds, pageInfoCache, getBundlePageDataByPageId],
  );

  const label = useMemo(() => {
    if (isLoading) return 'Extracting...';
    if (!pageIdsNotInLightBundle.length) return 'All matching pages extracted';
    return 'Extract all matching pages';
  }, [isLoading, pageIdsNotInLightBundle.length]);

  const handleClick = async () => {
    if (!bundleId || !pageIdsNotInLightBundle.length) return;

    const extractedPageIds = [...pageIdsNotInLightBundle];
    const pageWord = extractedPageIds.length === 1 ? 'page' : 'pages';
    // The search response is not guaranteed to be in bundle order, so take the earliest match.
    const extractedIdsSet = new Set(extractedPageIds);
    const firstMatchPageId = bundlePageIds.find((id) => extractedIdsSet.has(id)) ?? extractedPageIds[0];

    try {
      await triggerAddToLightBundle({ bundleId, pageIds: extractedPageIds }).unwrap();

      const cacheUpdates: Record<string, PageInfoData> = {};
      extractedPageIds.forEach((id) => {
        cacheUpdates[id] = { inLightBundle: true };
      });
      dispatch(mergePageInfo({ key: BundleType.FULL, data: cacheUpdates }));
      dispatch(
        api.util.invalidateTags([
          { type: BUNDLE_DUPLICATES_TAG, id: bundleId },
          { type: BUNDLE_SIMILARS_TAG, id: bundleId },
          { type: BUNDLE_UNDO_REDO_HISTORY_TAG, id: bundleId },
        ]),
      );

      // "Hide extracted pages" now excludes these pages, so the sections have to come back from the server.
      if (enabledStates[FILTER_EXTRACTED_KEY]) {
        refreshBundleSectionsWithCurrentFilters(dispatch, store.getState());
      }

      sendMessageWithoutResponse(BROADCAST_FORCE_TIMELINE_REFRESH, { reason: 'Extract success' });
      sendMessageWithoutResponse(BROADCAST_FORCE_CASE_OVERVIEW_CARDS_REFRESH, { reason: 'Extract success' });

      // Let an open Light Bundle tab refresh itself; we deliberately do not focus it.
      sendMessageWithResponse(
        BROADCAST_SCROLL_TO_PAGE_ID_IN_LIGHT_BUNDLE_WHEN_EXTRACTED_FROM_FULL_BUNDLE,
        { showPageIdInViewport: firstMatchPageId, bundleId: otherBundleId },
        1000,
      ).catch((error) => console.info(error.message));

      globalToastRef.current?.show({
        severity: 'success',
        summary: 'Extracted',
        detail: `All ${extractedPageIds.length} matching ${pageWord} added to the Light Bundle.`,
        life: 2500,
      });
    } catch (error) {
      console.error('Extract matching pages failed:', error);
      // The endpoint lock has already shown a toast for this
      if (isEndpointLockError(error)) return;
      globalToastRef.current?.show({
        severity: 'error',
        summary: 'Extract failed',
        detail: 'Failed to extract the matching pages to the Light Bundle. Please try again.',
        closable: true,
        life: 5000,
      });
    }
  };

  if (!isEditBundles || activeBundleType !== BundleType.FULL || !pageIds.length) {
    return null;
  }

  return (
    <Button
      label={label}
      loading={isLoading}
      disabled={isLoading || !pageIdsNotInLightBundle.length}
      onClick={handleClick}
      pt={{
        root: {
          className:
            'shrink-0 justify-center items-center whitespace-nowrap !h-[32px] !px-4 shadow-sm disabled:bg-white disabled:border-white disabled:text-fletchers-green-500 disabled:opacity-100',
        },
        label: { className: 'flex-none text-[14px]' },
        loadingIcon: { className: 'animate-spin mr-1' },
      }}
    />
  );
};
